import * as React from 'react'
import { ReactNode } from 'react'
import * as UUID from 'uuid'
import { Toast, ToastId, ToastIntent, ToastTitle, useToastController } from '@fluentui/react-components'
import { toasterId, useActiveToastIds } from '../components/Toast/AppToaster'
import PromiseToastContent, { PromiseToastRenderers } from '../components/Toast/PromiseToastContent'
import ToastDismiss from '../components/Toast/ToastDismiss'

/** Default lifetime of a toast, matching what the application used before. */
export const DEFAULT_TOAST_TIMEOUT = 30000

/** Keeps a toast on screen until it is dismissed explicitly. */
export const TOAST_TIMEOUT_NEVER = -1

export type AppToastOptions = {
    intent?: ToastIntent
    timeout?: number
}

export type AppToast = {
    /** Whether this toast is currently on screen. */
    isActive: (toastId: ToastId) => boolean
    /** Shows a one-off toast. Use `upsert` when the same toast has to be updated later. */
    show: (content: ReactNode, options?: AppToastOptions) => ToastId
    /** Shows a plain message. The most common case, so it does not need a Toast wrapper at the call site. */
    showMessage: (message: ReactNode, options?: AppToastOptions) => ToastId
    /** Shows a toast, or updates it when it is already on screen. */
    upsert: (toastId: ToastId, content: ReactNode, options?: AppToastOptions) => void
    /** Shows a toast that follows a promise and reports its outcome. Returns the promise unchanged. */
    promise: <T>(promise: Promise<T>, renderers: PromiseToastRenderers<T>) => Promise<T>
    dismiss: (toastId: ToastId) => void
    /** Dismisses every toast on screen that the predicate accepts. */
    dismissWhere: (matches: (toastId: ToastId) => boolean) => void
}

/**
 * The application's toasts. Always use this rather than useToastController directly, so that every toast lands in the
 * one toaster and the active-id bookkeeping stays correct.
 *
 * Note that `upsert` only sends content when the toast has to be created. Toast content is rendered inside the
 * toaster's own React tree, so a component that subscribes to whatever it displays keeps itself up to date; only the
 * toast's own options, such as its intent, are pushed on an update.
 */
export default function useAppToast(): AppToast {
    const { dispatchToast, updateToast, dismissToast } = useToastController(toasterId)
    const active = useActiveToastIds()

    const isActive = (toastId: ToastId) => active.has(toastId)

    const create = (toastId: ToastId, content: ReactNode, options?: AppToastOptions) => {
        active.add(toastId)
        dispatchToast(content, {
            toastId,
            timeout: DEFAULT_TOAST_TIMEOUT,
            ...options,
            onStatusChange: (_, { status }) => {
                // 'unmounted' fires once the exit animation finished and Fluent released the id for reuse.
                if (status === 'unmounted') {
                    active.delete(toastId)
                }
            },
        })
    }

    const show = (content: ReactNode, options?: AppToastOptions) => {
        const toastId = UUID.v4()
        create(toastId, content, options)
        return toastId
    }

    return {
        isActive,
        show,

        showMessage: (message: ReactNode, options?: AppToastOptions) =>
            show(
                <Toast>
                    <ToastTitle action={<ToastDismiss />}>{message}</ToastTitle>
                </Toast>,
                options,
            ),

        upsert: (toastId: ToastId, content: ReactNode, options?: AppToastOptions) => {
            if (isActive(toastId)) {
                updateToast({ toastId, ...options })
                return
            }

            create(toastId, content, options)
        },

        promise: <T,>(promise: Promise<T>, renderers: PromiseToastRenderers<T>) => {
            const toastId = UUID.v4()

            create(
                toastId,
                <PromiseToastContent
                    toastId={toastId}
                    promise={promise}
                    {...renderers}
                    onSettled={(id, intent) => updateToast({ toastId: id, intent, timeout: DEFAULT_TOAST_TIMEOUT })}
                />,
                // Stay on screen until the promise settles; the timeout starts from the update above.
                { intent: 'info', timeout: TOAST_TIMEOUT_NEVER },
            )

            return promise
        },

        dismiss: (toastId: ToastId) => dismissToast(toastId),

        // Copied first: dismissing releases the id, which takes it back out of the set being iterated.
        dismissWhere: (matches: (toastId: ToastId) => boolean) => [...active].filter(matches).forEach(toastId => dismissToast(toastId)),
    }
}
